import { TRANSPARENT_PIXEL } from '@/app/_lib/image-art-direction';
import { emptyToUndefined } from '@/app/_lib/utils';
import type { VideoPoster } from '@/entities/models/page';

// =============================================================================
// Types
// =============================================================================

export interface VideoSource { url: string; format: string }

export interface VideoSize {
  width: number;
  height: number;
  videoSources: VideoSource[];
  posterSources: VideoSource[];
  mediaQuery?: string;
}

// Video file from Typo3 (array format - used by quote, hero-advanced items)
export interface VideoFile {
  title?: string | null;
  description?: string | null;
  autoplay?: boolean;
  mimeType?: string;
  fileExtension?: string;
  url: string;
  poster?: VideoPoster | null;
}

// Video with desktop/mobile variants (used by contact-section)
export interface ResponsiveVideoSource {
  webm?: string;
  mp4?: string;
}

export interface ResponsiveVideo {
  desktop?: ResponsiveVideoSource;
  mobile?: ResponsiveVideoSource;
  autoplay?: boolean;
}

// Aspect ratio configuration
export interface AspectRatio {
  width: number;
  height: number;
}

// =============================================================================
// Internal Helpers
// =============================================================================

// Unique poster URLs to avoid internal key collision in ReactResponsiveVideo
const DESKTOP_POSTER: VideoSource[] = [{ url: `${TRANSPARENT_PIXEL}#desktop`, format: 'image/png' }];
const MOBILE_POSTER: VideoSource[] = [{ url: `${TRANSPARENT_PIXEL}#mobile`, format: 'image/png' }];
const DEFAULT_POSTER: VideoSource[] = [{ url: TRANSPARENT_PIXEL, format: 'image/png' }];

/**
 * Extract video sources from desktop/mobile format (webm/mp4)
 * webm comes first for better compression
 */
function getSourcesFromWebmMp4(source: ResponsiveVideoSource | undefined): VideoSource[] {
  if (!source) return [];
  const sources: VideoSource[] = [];
  // Only add sources with actual URLs (non-empty strings)
  if (source.webm && source.webm.trim() !== '') {
    sources.push({ url: source.webm, format: 'video/webm' });
  }
  if (source.mp4 && source.mp4.trim() !== '') {
    sources.push({ url: source.mp4, format: 'video/mp4' });
  }
  return sources;
}

/**
 * Extract video sources from file array format
 * Sorts webm first for better compression
 */
function getSourcesFromFileArray(files: VideoFile[]): VideoSource[] {
  // Filter out files without valid URLs
  const validFiles = files.filter((file) => file.url && file.url.trim() !== '');

  const sorted = [...validFiles].sort((a, b) => {
    if (a.fileExtension === 'webm') return -1;
    if (b.fileExtension === 'webm') return 1;
    return 0;
  });

  return sorted.map((file) => ({
    url: file.url,
    format:
      emptyToUndefined(file.mimeType) ??
      `video/${emptyToUndefined(file.fileExtension) ?? 'mp4'}`,
  }));
}

/**
 * Infer image format from URL extension
 */
function getImageFormatFromUrl(url: string): string {
  const ext = url.split('.').pop()?.toLowerCase();
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'png') return 'image/png';
  return 'image/webp';
}

/**
 * Extract poster source from video files if available
 * Returns the poster from the first file that has one, or fallback
 */
function getPosterFromFiles(files: VideoFile[] | undefined, fallback: VideoSource[]): VideoSource[] {
  if (!files || files.length === 0) return fallback;

  const fileWithPoster = files.find(
    (file) => file.poster?.imageUrl && file.poster.imageUrl.trim() !== ''
  );
  if (!fileWithPoster?.poster) return fallback;

  const url = fileWithPoster.poster.imageUrl;
  return [{ url, format: getImageFormatFromUrl(url) }];
}

// =============================================================================
// Public API
// =============================================================================

/**
 * Build video sizes for ReactResponsiveVideo from responsive video (desktop/mobile webm/mp4)
 * Used by: contact-section
 *
 * @param video - Video object with desktop/mobile sources
 * @param desktopAspect - Aspect ratio for desktop
 * @param mobileAspect - Aspect ratio for mobile
 * @param desktopMediaQuery - Media query for desktop (e.g., '(min-width: 1024px)')
 */
export function buildResponsiveVideoSizes(
  video: ResponsiveVideo,
  desktopAspect: AspectRatio,
  mobileAspect: AspectRatio,
  desktopMediaQuery: string
): VideoSize[] {
  const desktopSources = getSourcesFromWebmMp4(video.desktop);
  const mobileSources = getSourcesFromWebmMp4(video.mobile);

  const hasDesktop = desktopSources.length > 0;
  const hasMobile = mobileSources.length > 0;

  // No sources at all
  if (!hasDesktop && !hasMobile) {
    return [];
  }

  // Only mobile sources - single entry, no media query needed
  if (!hasDesktop && hasMobile) {
    return [{
      width: mobileAspect.width,
      height: mobileAspect.height,
      videoSources: mobileSources,
      posterSources: DEFAULT_POSTER,
    }];
  }

  // Only desktop sources - single entry, no media query needed
  if (hasDesktop && !hasMobile) {
    return [{
      width: desktopAspect.width,
      height: desktopAspect.height,
      videoSources: desktopSources,
      posterSources: DEFAULT_POSTER,
    }];
  }

  // Both desktop and mobile - art direction with media query
  return [
    {
      width: desktopAspect.width,
      height: desktopAspect.height,
      videoSources: desktopSources,
      posterSources: DESKTOP_POSTER,
      mediaQuery: desktopMediaQuery,
    },
    {
      width: mobileAspect.width,
      height: mobileAspect.height,
      videoSources: mobileSources,
      posterSources: MOBILE_POSTER,
    },
  ];
}

/**
 * Build video sizes for ReactResponsiveVideo from video file arrays (desktop/mobile)
 * Used by: hero-advanced (fullscreen/halfsize videos)
 *
 * @param desktopFiles - Array of video files for desktop
 * @param mobileFiles - Array of video files for mobile
 * @param desktopAspect - Aspect ratio for desktop
 * @param mobileAspect - Aspect ratio for mobile
 * @param desktopMediaQuery - Media query for desktop
 */
export function buildResponsiveVideoSizesFromFiles(
  desktopFiles: VideoFile[] | undefined,
  mobileFiles: VideoFile[] | undefined,
  desktopAspect: AspectRatio,
  mobileAspect: AspectRatio,
  desktopMediaQuery: string
): VideoSize[] {
  const desktopSources = desktopFiles ? getSourcesFromFileArray(desktopFiles) : [];
  const mobileSources = mobileFiles ? getSourcesFromFileArray(mobileFiles) : [];

  const hasDesktop = desktopSources.length > 0;
  const hasMobile = mobileSources.length > 0;

  // No sources at all
  if (!hasDesktop && !hasMobile) {
    return [];
  }

  // Only mobile sources - single entry, no media query needed
  if (!hasDesktop && hasMobile) {
    return [{
      width: mobileAspect.width,
      height: mobileAspect.height,
      videoSources: mobileSources,
      posterSources: getPosterFromFiles(mobileFiles, DEFAULT_POSTER),
    }];
  }

  // Only desktop sources - single entry, no media query needed
  if (hasDesktop && !hasMobile) {
    return [{
      width: desktopAspect.width,
      height: desktopAspect.height,
      videoSources: desktopSources,
      posterSources: getPosterFromFiles(desktopFiles, DEFAULT_POSTER),
    }];
  }

  // Both desktop and mobile - art direction with media query
  return [
    {
      width: desktopAspect.width,
      height: desktopAspect.height,
      videoSources: desktopSources,
      posterSources: getPosterFromFiles(desktopFiles, DESKTOP_POSTER),
      mediaQuery: desktopMediaQuery,
    },
    {
      width: mobileAspect.width,
      height: mobileAspect.height,
      videoSources: mobileSources,
      posterSources: getPosterFromFiles(mobileFiles, MOBILE_POSTER),
    },
  ];
}

/**
 * Build video sizes for ReactResponsiveVideo from simple video file array (no art direction)
 * Used by: quote
 *
 * @param files - Array of video files (different formats of same video)
 * @param aspect - Aspect ratio (same for all viewports)
 */
export function buildSimpleVideoSizes(
  files: VideoFile[],
  aspect: AspectRatio
): VideoSize[] {
  const sources = getSourcesFromFileArray(files);
  if (sources.length === 0) return [];

  return [
    {
      width: aspect.width,
      height: aspect.height,
      videoSources: sources,
      posterSources: getPosterFromFiles(files, DEFAULT_POSTER),
    },
  ];
}

